/**
 * Full Video Section Component
 * Handles video play on click, cursor-following play button on hover
 * Automatically pauses when section leaves viewport and resumes when re-entering
 * Supports MP4, YouTube, and Vimeo videos
 * Supports multiple video sections on the same page
 */

// Global mouse tracking shared across all instances
let globalMouseX = null;
let globalMouseY = null;
let hasGlobalMouseMoved = false;
let globalMouseTrackingInitialized = false;

export function fullVideoSection() {
    const sections = document.querySelectorAll('.full-video-section');
    if (!sections.length) return;

    // Initialize global mouse tracking once for all sections
    if (!globalMouseTrackingInitialized) {
        document.addEventListener('mousemove', (e) => {
            hasGlobalMouseMoved = true;
            globalMouseX = e.clientX;
            globalMouseY = e.clientY;
        });
        globalMouseTrackingInitialized = true;
    }

    // Initialize each section independently
    sections.forEach((section, index) => {
        initializeVideoSection(section, index);
    });
}

/**
 * Initialize a single video section
 */
function initializeVideoSection(section, sectionIndex) {
    // Get video configuration from data attributes
    const videoType = section.getAttribute('data-video-type');
    const videoSrc = section.getAttribute('data-video-src');
    let videoId = section.getAttribute('data-video-id');
    
    // For YouTube/Vimeo, if data-video-id is not set, use data-video-src as fallback
    if (!videoId && videoSrc && (videoType?.toLowerCase() === 'youtube' || videoType?.toLowerCase() === 'vimeo')) {
        videoId = videoSrc;
    }
    
    // If no video configuration, don't initialize
    if (!videoType || (!videoSrc && !videoId)) return;
    
    const videoTypeLower = videoType.toLowerCase();
    const videoContainer = section.querySelector('.full-video-section__video-container') || section;
    const playButton = section.querySelector('.full-video-section__play-button');
    const background = section.querySelector('.full-video-section__background');
    const textWrapper = section.querySelector('.full-video-section__wrapper');
    
    let video = null; // Will be set based on video type
    let youtubePlayer = null;
    let vimeoPlayer = null;
    let isPlaying = false;
    let wasPlayingBeforeViewportExit = false; // Track if video was playing before leaving viewport
    let mouseX = 0;
    let mouseY = 0;
    let buttonX = 0;
    let buttonY = 0;
    let rafId = null;
    let lastMouseEvent = null;

    // Initialize video based on type
    function initializeVideo() {
        switch (videoTypeLower) {
            case 'mp4':
                if (!videoSrc) return;
                video = section.querySelector('.full-video-section__video');
                if (!video) {
                    video = document.createElement('video');
                    video.className = 'full-video-section__video';
                    video.preload = 'metadata';
                    video.loop = true;
                    video.muted = true;
                    video.playsInline = true;
                    video.controls = false;
                    video.style.pointerEvents = 'none';
                    videoContainer.appendChild(video);
                }
                video.src = videoSrc;
                video.muted = true;
                video.loop = true;
                break;
                
            case 'youtube':
                if (!videoId) return;
                createYouTubePlayer(videoId, videoContainer);
                break;
                
            case 'vimeo':
                if (!videoId) return;
                createVimeoPlayer(videoId, videoContainer);
                break;
        }
    }

    /**
     * Create YouTube player
     */
    function createYouTubePlayer(videoId, container) {
        const origin = window.location.origin;
        
        // Try to use YouTube IFrame API if available
        if (typeof YT !== 'undefined' && YT.Player) {
            const playerDiv = document.createElement('div');
            playerDiv.className = 'full-video-section__youtube-player';
            playerDiv.id = `youtube-player-full-video-${sectionIndex}-${Date.now()}`;
            container.appendChild(playerDiv);
            
            youtubePlayer = new YT.Player(playerDiv.id, {
                videoId: videoId,
                playerVars: {
                    autoplay: 0,
                    mute: 1,
                    loop: 1,
                    playlist: videoId,
                    controls: 0,
                    showinfo: 0,
                    rel: 0,
                    modestbranding: 1,
                    playsinline: 1,
                    enablejsapi: 1,
                    origin: origin
                },
                events: {
                    onReady: (event) => {
                        youtubePlayer = event.target;
                    },
                    onError: (event) => {
                        console.warn('YouTube player error:', event.data);
                    }
                }
            });
        } else {
            // Load YouTube IFrame API if not already loaded
            if (typeof YT === 'undefined') {
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                // Wait for API to load
                const originalCallback = window.onYouTubeIframeAPIReady;
                window.onYouTubeIframeAPIReady = () => {
                    if (originalCallback) originalCallback();
                    createYouTubePlayer(videoId, container);
                };
            } else {
                // Fallback: use direct iframe embed
                const iframe = document.createElement('iframe');
                iframe.className = 'full-video-section__youtube-player';
                iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${origin}`;
                iframe.allow = 'autoplay; encrypted-media';
                iframe.allowFullscreen = false;
                container.appendChild(iframe);
            }
        }
    }

    /**
     * Create Vimeo player
     */
    function createVimeoPlayer(videoId, container) {
        const iframe = document.createElement('iframe');
        iframe.className = 'full-video-section__vimeo-player';
        iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=0&muted=1&loop=1&background=0&controls=0&responsive=1`;
        iframe.allow = 'autoplay; fullscreen';
        iframe.allowFullscreen = true;
        container.appendChild(iframe);
        
        // Load Vimeo Player API if not already loaded
        if (typeof Vimeo === 'undefined') {
            const tag = document.createElement('script');
            tag.src = 'https://player.vimeo.com/api/player.js';
            tag.onload = () => {
                if (iframe && typeof Vimeo !== 'undefined') {
                    try {
                        vimeoPlayer = new Vimeo.Player(iframe);
                    } catch (err) {
                        console.warn('Error initializing Vimeo player:', err);
                    }
                }
            };
            document.head.appendChild(tag);
        } else {
            try {
                vimeoPlayer = new Vimeo.Player(iframe);
            } catch (err) {
                console.warn('Error initializing Vimeo player:', err);
            }
        }
    }

    // Update button position smoothly
    function updateButtonPosition() {
        // Recalculate mouse position if we have a last mouse event (handles scroll)
        if (lastMouseEvent) {
            const pos = calculateMousePosition(lastMouseEvent);
            mouseX = pos.x;
            mouseY = pos.y;
        }
        
        const speed = 0.15; // Easing speed (0-1, lower = smoother)
        buttonX += (mouseX - buttonX) * speed;
        buttonY += (mouseY - buttonY) * speed;

        playButton.style.left = `${buttonX}px`;
        playButton.style.top = `${buttonY}px`;
        
        rafId = requestAnimationFrame(updateButtonPosition);
    }

    // Calculate mouse position relative to section
    function calculateMousePosition(e) {
        const rect = section.getBoundingClientRect();
        let x = e.clientX - rect.left - 70; // Offset by half button width (140/2)
        let y = e.clientY - rect.top - 24; // Offset by half button height (48/2)
        
        // Clamp to section bounds
        x = Math.max(0, Math.min(x, rect.width - 140));
        y = Math.max(0, Math.min(y, rect.height - 48));
        
        return { x, y };
    }

    // Check if mouse is over text wrapper
    function isMouseOverText(e) {
        if (!textWrapper) return false;
        const rect = textWrapper.getBoundingClientRect();
        return (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
        );
    }

    // Check if mouse coordinates are over the section
    function isMouseOverSection(x, y) {
        const rect = section.getBoundingClientRect();
        return (
            x >= rect.left &&
            x <= rect.right &&
            y >= rect.top &&
            y <= rect.bottom
        );
    }

    // Handle mouse move
    function handleMouseMove(e) {
        lastMouseEvent = e;
        
        // Don't show play button if hovering over text
        if (isMouseOverText(e)) {
            if (playButton.classList.contains('full-video-section__play-button--visible')) {
                playButton.classList.remove('full-video-section__play-button--visible');
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
            }
            return;
        }
        
        const pos = calculateMousePosition(e);
        mouseX = pos.x;
        mouseY = pos.y;
        
        // Show button if video is paused and mouse is over section
        if (!isPlaying && !playButton.classList.contains('full-video-section__play-button--visible')) {
            playButton.classList.add('full-video-section__play-button--visible');
            buttonX = mouseX;
            buttonY = mouseY;
            if (!rafId) {
                rafId = requestAnimationFrame(updateButtonPosition);
            }
        }
    }

    // Show play button on hover
    function handleMouseEnter(e) {
        if (!isPlaying && !isMouseOverText(e)) {
            // Calculate mouse position from the event to position button at cursor immediately
            const pos = calculateMousePosition(e);
            mouseX = pos.x;
            mouseY = pos.y;
            buttonX = mouseX; // Set button position to cursor position immediately
            buttonY = mouseY;
            lastMouseEvent = e; // Store event for scroll handling
            
            playButton.classList.add('full-video-section__play-button--visible');
            rafId = requestAnimationFrame(updateButtonPosition);
        }
    }

    // Hide play button on leave
    function handleMouseLeave() {
        if (!isPlaying) {
            playButton.classList.remove('full-video-section__play-button--visible');
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        }
    }

    // Play video (works for MP4, YouTube, and Vimeo)
    function playVideo() {
        if (!isPlaying) {
            wasPlayingBeforeViewportExit = false; // Reset flag on manual play
            // Add class immediately when video is played
            section.classList.add('full-video-section--playing');
            playButton.classList.remove('full-video-section__play-button--visible');
            
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            
            if (videoTypeLower === 'mp4' && video) {
                video.play().then(() => {
                    isPlaying = true;
                }).catch((error) => {
                    console.error('Error playing video:', error);
                    section.classList.remove('full-video-section--playing');
                });
            } else if (videoTypeLower === 'youtube') {
                if (youtubePlayer && typeof youtubePlayer.playVideo === 'function') {
                    try {
                        youtubePlayer.playVideo();
                        isPlaying = true;
                    } catch (err) {
                        console.error('Error playing YouTube video:', err);
                        section.classList.remove('full-video-section--playing');
                    }
                } else {
                    // Fallback: update iframe URL to trigger autoplay
                    const iframe = videoContainer.querySelector('iframe');
                    if (iframe?.src) {
                        const url = new URL(iframe.src);
                        url.searchParams.set('autoplay', '1');
                        iframe.src = url.toString();
                        isPlaying = true;
                    }
                }
            } else if (videoTypeLower === 'vimeo') {
                if (vimeoPlayer && typeof vimeoPlayer.play === 'function') {
                    vimeoPlayer.play().then(() => {
                        isPlaying = true;
                    }).catch((error) => {
                        console.error('Error playing Vimeo video:', error);
                        section.classList.remove('full-video-section--playing');
                    });
                } else {
                    // Fallback: update iframe URL to trigger autoplay
                    const iframe = videoContainer.querySelector('iframe');
                    if (iframe?.src) {
                        const url = new URL(iframe.src);
                        url.searchParams.set('autoplay', '1');
                        iframe.src = url.toString();
                        isPlaying = true;
                    }
                }
            }
        }
    }

    // Pause video when out of viewport (keeps video visible)
    function pauseVideoOnViewportExit() {
        if (isPlaying) {
            wasPlayingBeforeViewportExit = true;
            
            if (videoTypeLower === 'mp4' && video) {
                video.pause();
                isPlaying = false;
            } else if (videoTypeLower === 'youtube') {
                if (youtubePlayer && typeof youtubePlayer.pauseVideo === 'function') {
                    try {
                        youtubePlayer.pauseVideo();
                        isPlaying = false;
                    } catch (err) {
                        console.warn('Error pausing YouTube video:', err);
                    }
                }
            } else if (videoTypeLower === 'vimeo') {
                if (vimeoPlayer && typeof vimeoPlayer.pause === 'function') {
                    vimeoPlayer.pause().then(() => {
                        isPlaying = false;
                    }).catch((error) => {
                        console.warn('Error pausing Vimeo video:', error);
                    });
                }
            }
            // Don't remove 'full-video-section--playing' class to keep video visible
        }
    }

    // Resume video when re-entering viewport
    function resumeVideoOnViewportEnter() {
        if (wasPlayingBeforeViewportExit && !isPlaying) {
            if (videoTypeLower === 'mp4' && video) {
                video.play().then(() => {
                    isPlaying = true;
                    wasPlayingBeforeViewportExit = false;
                    section.classList.add('full-video-section--playing');
                    playButton.classList.remove('full-video-section__play-button--visible');
                    
                    if (rafId) {
                        cancelAnimationFrame(rafId);
                        rafId = null;
                    }
                }).catch((error) => {
                    console.error('Error resuming video:', error);
                    wasPlayingBeforeViewportExit = false;
                });
            } else if (videoTypeLower === 'youtube') {
                if (youtubePlayer && typeof youtubePlayer.playVideo === 'function') {
                    try {
                        youtubePlayer.playVideo();
                        isPlaying = true;
                        wasPlayingBeforeViewportExit = false;
                        section.classList.add('full-video-section--playing');
                        playButton.classList.remove('full-video-section__play-button--visible');
                        
                        if (rafId) {
                            cancelAnimationFrame(rafId);
                            rafId = null;
                        }
                    } catch (err) {
                        console.error('Error resuming YouTube video:', err);
                        wasPlayingBeforeViewportExit = false;
                    }
                }
            } else if (videoTypeLower === 'vimeo') {
                if (vimeoPlayer && typeof vimeoPlayer.play === 'function') {
                    vimeoPlayer.play().then(() => {
                        isPlaying = true;
                        wasPlayingBeforeViewportExit = false;
                        section.classList.add('full-video-section--playing');
                        playButton.classList.remove('full-video-section__play-button--visible');
                        
                        if (rafId) {
                            cancelAnimationFrame(rafId);
                            rafId = null;
                        }
                    }).catch((error) => {
                        console.error('Error resuming Vimeo video:', error);
                        wasPlayingBeforeViewportExit = false;
                    });
                }
            }
        }
    }

    // Handle scroll to update button position and check if mouse enters section
    function handleScroll() {
        if (isPlaying || !hasGlobalMouseMoved) return;
        
        // Check if mouse is currently over the section (only if we have valid coordinates)
        if (globalMouseX !== null && globalMouseY !== null && isMouseOverSection(globalMouseX, globalMouseY)) {
            // Create a synthetic mouse event for position calculation
            const syntheticEvent = {
                clientX: globalMouseX,
                clientY: globalMouseY
            };
            
            // Don't show button if hovering over text
            if (textWrapper) {
                const textRect = textWrapper.getBoundingClientRect();
                if (
                    globalMouseX >= textRect.left &&
                    globalMouseX <= textRect.right &&
                    globalMouseY >= textRect.top &&
                    globalMouseY <= textRect.bottom
                ) {
                    // Mouse is over text, hide button if visible
                    if (playButton.classList.contains('full-video-section__play-button--visible')) {
                        playButton.classList.remove('full-video-section__play-button--visible');
                        if (rafId) {
                            cancelAnimationFrame(rafId);
                            rafId = null;
                        }
                    }
                    return;
                }
            }
            
            // Update lastMouseEvent for position calculation
            lastMouseEvent = syntheticEvent;
            
            const pos = calculateMousePosition(syntheticEvent);
            mouseX = pos.x;
            mouseY = pos.y;
            
            // Show button if not already visible
            if (!playButton.classList.contains('full-video-section__play-button--visible')) {
                playButton.classList.add('full-video-section__play-button--visible');
                buttonX = mouseX;
                buttonY = mouseY;
                if (!rafId) {
                    rafId = requestAnimationFrame(updateButtonPosition);
                }
            }
        } else {
            // Mouse is not over section - hide button if visible
            if (playButton.classList.contains('full-video-section__play-button--visible')) {
                playButton.classList.remove('full-video-section__play-button--visible');
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
            }
        }
    }

    // Get center position of section for button placement
    function getCenterPosition() {
        const rect = section.getBoundingClientRect();
        return {
            x: (rect.width / 2) - 70, // Center minus half button width
            y: (rect.height / 2) - 24  // Center minus half button height
        };
    }

    // Intersection Observer to pause/resume when section leaves/enters viewport
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting && isPlaying) {
                // Section left viewport - pause video
                pauseVideoOnViewportExit();
            } else if (entry.isIntersecting) {
                // Section entered viewport
                if (wasPlayingBeforeViewportExit) {
                    // Resume video if it was playing before
                    resumeVideoOnViewportEnter();
                } else if (!isPlaying && hasGlobalMouseMoved) {
                    // Show button when section enters viewport (only if mouse has moved)
                    let targetX, targetY;
                    let syntheticEvent;
                    
                    // Check if mouse is over section (and we have valid mouse coordinates)
                    if (globalMouseX !== null && globalMouseY !== null) {
                        // We have tracked mouse position, check if it's over section
                        if (isMouseOverSection(globalMouseX, globalMouseY)) {
                            syntheticEvent = {
                                clientX: globalMouseX,
                                clientY: globalMouseY
                            };
                            
                            // Don't show button if hovering over text
                            if (textWrapper) {
                                const textRect = textWrapper.getBoundingClientRect();
                                if (
                                    globalMouseX >= textRect.left &&
                                    globalMouseX <= textRect.right &&
                                    globalMouseY >= textRect.top &&
                                    globalMouseY <= textRect.bottom
                                ) {
                                    // Mouse is over text, position button at center instead
                                    const centerPos = getCenterPosition();
                                    targetX = centerPos.x;
                                    targetY = centerPos.y;
                                } else {
                                    // Mouse is over section (not over text), use mouse position
                                    const pos = calculateMousePosition(syntheticEvent);
                                    targetX = pos.x;
                                    targetY = pos.y;
                                    lastMouseEvent = syntheticEvent;
                                }
                            } else {
                                // No text wrapper, use mouse position
                                const pos = calculateMousePosition(syntheticEvent);
                                targetX = pos.x;
                                targetY = pos.y;
                                lastMouseEvent = syntheticEvent;
                            }
                        } else {
                            // Mouse is not over section, position button at center
                            const centerPos = getCenterPosition();
                            targetX = centerPos.x;
                            targetY = centerPos.y;
                        }
                    } else {
                        // No mouse movement tracked yet, position button at center
                        const centerPos = getCenterPosition();
                        targetX = centerPos.x;
                        targetY = centerPos.y;
                    }
                    
                    // Set button position
                    mouseX = targetX;
                    mouseY = targetY;
                    buttonX = targetX;
                    buttonY = targetY;
                    
                    // Show button
                    if (!playButton.classList.contains('full-video-section__play-button--visible')) {
                        playButton.classList.add('full-video-section__play-button--visible');
                        if (!rafId) {
                            rafId = requestAnimationFrame(updateButtonPosition);
                        }
                    }
                }
            }
        });
    }, {
        threshold: 0.1 // Trigger when less than 10% visible
    });
    
    observer.observe(section);

    // Initialize video
    initializeVideo();

    // Event listeners
    section.addEventListener('mouseenter', handleMouseEnter);
    section.addEventListener('mouseleave', handleMouseLeave);
    section.addEventListener('mousemove', handleMouseMove);
    section.addEventListener('click', playVideo);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scroll events
    
    // Hide play button when hovering over text wrapper
    if (textWrapper) {
        textWrapper.addEventListener('mouseenter', () => {
            if (!isPlaying && playButton.classList.contains('full-video-section__play-button--visible')) {
                playButton.classList.remove('full-video-section__play-button--visible');
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
            }
        });
    }
    
    // Keyboard accessibility
    section.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            playVideo();
        }
    });

    // Set tabindex for keyboard accessibility
    section.setAttribute('tabindex', '0');
    section.setAttribute('role', 'button');
    section.setAttribute('aria-label', 'Click to play video');
}
